import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Popconfirm,
  message,
  Input,
  Select,
  Card,
  Row,
  Col,
  Statistic,
  Tabs,
  Modal,
} from 'antd';
import {
  DeleteOutlined,
  UndoOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import OptimizedProviderForm from './OptimizedProviderForm';

const { Option } = Select;

export default function ProvidersManagement() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterServer, setFilterServer] = useState('all');
  const [filterZone, setFilterZone] = useState('all');
  const [showOptimizedForm, setShowOptimizedForm] = useState(false);
  const [activeTab, setActiveTab] = useState('zones');
  const [selectedZone, setSelectedZone] = useState(null);
  const [zoneBroadcasts, setZoneBroadcasts] = useState([]);
  const [loadingBroadcasts, setLoadingBroadcasts] = useState(false);
  const [showBroadcastsModal, setShowBroadcastsModal] = useState(false);
  const [zoneSearchText, setZoneSearchText] = useState('');
  const [zoneFilterServer, setZoneFilterServer] = useState('all');
  const [zoneFilterStatus, setZoneFilterStatus] = useState('all');

  const token = localStorage.getItem('prae.jwt');

  // Fetch providers
  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        include_deleted: showDeleted ? 'true' : 'false',
        with_broadcast_count: 'true',
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/smtp-providers?${params}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch providers');

      const data = await response.json();
      setProviders(data || []);
    } catch (error) {
      console.error('Error:', error);
      message.error('Erreur lors du chargement des providers');
    } finally {
      setLoading(false);
    }
  }, [showDeleted, token]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // Soft delete
  const handleSoftDelete = async (id, name) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/smtp-providers/${id}`,
        {
          method: 'DELETE',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        if (response.status === 400) {
          message.error(
            `Impossible de supprimer: ${error.activeBroadcastsCount} broadcast(s) actif(s)`
          );
        } else {
          throw new Error('Delete failed');
        }
        return;
      }

      message.success(`Provider ${name} supprimé avec succès`);
      fetchProviders();
    } catch (error) {
      console.error('Error:', error);
      message.error('Erreur lors de la suppression');
    }
  };

  // Restore
  const handleRestore = async (id, name) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/smtp-providers/${id}/restore`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) throw new Error('Restore failed');

      message.success(`Provider ${name} restauré avec succès`);
      fetchProviders();
    } catch (error) {
      console.error('Error:', error);
      message.error('Erreur lors de la restauration');
    }
  };

  // Callback when providers are created
  const handleProviderCreated = () => {
    fetchProviders();
    setShowOptimizedForm(false);
  };

  // Fetch broadcasts for a zone
  const fetchZoneBroadcasts = async zone => {
    setLoadingBroadcasts(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        pageSize: '1000',
        zone: zone,
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/broadcasts/dashboard?${params}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch broadcasts');

      const result = await response.json();
      const data = result.broadcasts || [];

      // Sort: active first, then by creation date
      const sorted = data.sort((a, b) => {
        const aActive = a.status === 'active' ? 1 : 0;
        const bActive = b.status === 'active' ? 1 : 0;

        if (bActive !== aActive) {
          return bActive - aActive; // Active first
        }

        return new Date(b.created_at) - new Date(a.created_at);
      });

      setZoneBroadcasts(sorted);
    } catch (error) {
      console.error('Error:', error);
      message.error('Erreur lors du chargement des broadcasts');
    } finally {
      setLoadingBroadcasts(false);
    }
  };

  // Handle zone row click
  const handleZoneClick = zone => {
    setSelectedZone(zone);
    setShowBroadcastsModal(true);
    fetchZoneBroadcasts(zone);
  };

  // Get unique servers and zones for filters
  const uniqueServers = [
    ...new Set(providers.map(p => p.server).filter(Boolean)),
  ].sort();
  const allZones = [
    ...new Set(providers.map(p => p.zone).filter(Boolean)),
  ].sort();

  // Get zones for selected server
  const availableZones =
    filterServer === 'all'
      ? allZones
      : [
          ...new Set(
            providers
              .filter(p => p.server === filterServer)
              .map(p => p.zone)
              .filter(Boolean)
          ),
        ].sort();

  // Handle server change - reset zone if not available
  const handleServerChange = value => {
    setFilterServer(value);
    if (value !== 'all') {
      setFilterZone('all');
    }
  };

  // Filter providers
  const filteredProviders = providers.filter(provider => {
    // Search filter
    const searchLower = searchText.toLowerCase();
    const matchesSearch =
      !searchText ||
      provider.name?.toLowerCase().includes(searchLower) ||
      provider.domain?.toLowerCase().includes(searchLower) ||
      provider.zone?.toLowerCase().includes(searchLower) ||
      provider.server?.toLowerCase().includes(searchLower) ||
      provider.host?.toLowerCase().includes(searchLower);

    // Server filter
    const matchesServer =
      filterServer === 'all' || provider.server === filterServer;

    // Zone filter
    const matchesZone = filterZone === 'all' || provider.zone === filterZone;

    return matchesSearch && matchesServer && matchesZone;
  });

  // Calculate stats by zone
  const allZoneStats = allZones.map(zone => {
    const zoneProviders = providers.filter(
      p => p.zone === zone && !p.deleted_at
    );
    const activeCount = zoneProviders.filter(
      p => p.activeBroadcastsCount > 0
    ).length;
    const servers = [
      ...new Set(zoneProviders.map(p => p.server).filter(Boolean)),
    ].sort();

    return {
      zone,
      servers,
      total: zoneProviders.length,
      active: activeCount,
      inactive: zoneProviders.length - activeCount,
    };
  });

  // Filter zones based on search and filters
  const filteredZoneStats = allZoneStats.filter(zoneStat => {
    // Search filter
    const matchesSearch =
      !zoneSearchText ||
      zoneStat.zone.toLowerCase().includes(zoneSearchText.toLowerCase());

    // Server filter
    const matchesServer =
      zoneFilterServer === 'all' || zoneStat.servers.includes(zoneFilterServer);

    // Status filter
    let matchesStatus = true;
    if (zoneFilterStatus === 'active') {
      matchesStatus = zoneStat.active > 0;
    } else if (zoneFilterStatus === 'inactive') {
      matchesStatus = zoneStat.active === 0;
    }

    return matchesSearch && matchesServer && matchesStatus;
  });

  // Colonnes du tableau zones
  const zoneColumns = [
    {
      title: 'Zone',
      dataIndex: 'zone',
      key: 'zone',
      width: 250,
      sorter: (a, b) => a.zone.localeCompare(b.zone),
      render: zone => (
        <div
          className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
          onClick={() => handleZoneClick(zone)}
        >
          {zone}
        </div>
      ),
    },
    {
      title: 'Serveur(s)',
      dataIndex: 'servers',
      key: 'servers',
      width: 200,
      render: servers => servers.join(', ') || '-',
    },
    {
      title: 'Total Providers',
      dataIndex: 'total',
      key: 'total',
      width: 140,
      align: 'center',
      sorter: (a, b) => a.total - b.total,
      render: total => <Tag color="blue">{total}</Tag>,
    },
    {
      title: 'Actifs (avec broadcasts)',
      dataIndex: 'active',
      key: 'active',
      width: 180,
      align: 'center',
      sorter: (a, b) => a.active - b.active,
      defaultSortOrder: 'descend',
      render: active => (
        <Tag color={active > 0 ? 'green' : 'default'}>{active}</Tag>
      ),
    },
    {
      title: 'Inactifs (sans broadcasts)',
      dataIndex: 'inactive',
      key: 'inactive',
      width: 180,
      align: 'center',
      sorter: (a, b) => a.inactive - b.inactive,
      render: inactive => <Tag color="default">{inactive}</Tag>,
    },
  ];

  // Colonnes du tableau providers
  const providerColumns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => (
        <div>
          <div className="font-medium">{name}</div>
          {record.deleted_at && (
            <Tag color="red" className="mt-1">
              Supprimé
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Domaine',
      dataIndex: 'domain',
      key: 'domain',
      width: 180,
      sorter: (a, b) => (a.domain || '').localeCompare(b.domain || ''),
      render: domain => domain || '-',
    },
    {
      title: 'Serveur',
      dataIndex: 'server',
      key: 'server',
      width: 120,
      sorter: (a, b) => (a.server || '').localeCompare(b.server || ''),
      render: server => server || '-',
    },
    {
      title: 'Zone',
      dataIndex: 'zone',
      key: 'zone',
      width: 180,
      sorter: (a, b) => (a.zone || '').localeCompare(b.zone || ''),
      render: zone => zone || '-',
    },
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host',
      width: 150,
      render: (host, record) => `${host}:${record.port}`,
    },
    {
      title: 'Broadcasts Actifs',
      dataIndex: 'activeBroadcastsCount',
      key: 'activeBroadcastsCount',
      width: 140,
      sorter: (a, b) => a.activeBroadcastsCount - b.activeBroadcastsCount,
      defaultSortOrder: 'descend',
      render: count => (
        <Tag color={count > 0 ? 'blue' : 'default'}>{count || 0}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => {
        if (record.deleted_at) {
          return (
            <Popconfirm
              title="Restaurer ce provider ?"
              onConfirm={() => handleRestore(record.id, record.name)}
              okText="Oui"
              cancelText="Non"
            >
              <Button type="link" icon={<UndoOutlined />} size="small">
                Restaurer
              </Button>
            </Popconfirm>
          );
        }

        const hasActiveBC = record.activeBroadcastsCount > 0;

        return (
          <Popconfirm
            title={
              hasActiveBC
                ? `Impossible: ${record.activeBroadcastsCount} broadcast(s) actif(s)`
                : 'Supprimer ce provider ?'
            }
            onConfirm={() => handleSoftDelete(record.id, record.name)}
            okText="Oui"
            cancelText="Non"
            disabled={hasActiveBC}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
              disabled={hasActiveBC}
            >
              Supprimer
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  // Colonnes du tableau broadcasts (modal)
  const broadcastColumns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: name => <div className="font-medium">{name}</div>,
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: status => {
        const statusConfig = {
          active: { color: 'green', text: 'Actif' },
          paused: { color: 'orange', text: 'En pause' },
          completed: { color: 'blue', text: 'Terminé' },
          new: { color: 'default', text: 'Nouveau' },
        };
        const config = statusConfig[status] || statusConfig.new;
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Provider',
      key: 'provider',
      width: 180,
      render: (_, record) => {
        // Try to find provider by ID from the providers state
        const provider = providers.find(p => p.id === record.smtp_provider_id);
        return provider ? provider.name : record.smtp_provider_name || '-';
      },
    },
    {
      title: 'Jour',
      key: 'day',
      width: 80,
      align: 'center',
      render: (_, record) =>
        `${record.current_day || 0}/${record.warmup_total_days || 0}`,
    },
    {
      title: 'Date de début',
      dataIndex: 'scheduled_start_at',
      key: 'scheduled_start_at',
      width: 110,
      render: date => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
    },
    {
      title: 'Date de fin prévue',
      key: 'scheduled_end_at',
      width: 120,
      render: (_, record) => {
        if (!record.scheduled_start_at || !record.warmup_total_days) return '-';

        const startDate = new Date(record.scheduled_start_at);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + record.warmup_total_days);

        return endDate.toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen mt-16">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          Gestion des Serveurs SMTP
        </h1>

        {/* Barre d'outils - visible seulement pour l'onglet Provider */}
        {activeTab === 'providers' && (
          <Card className="mb-4">
            <Row gutter={[16, 16]} align="middle">
              <Col flex="auto">
                <Space size="middle" wrap>
                  <Input
                    placeholder="Rechercher un provider..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                    allowClear
                  />

                  <Select
                    placeholder="Tous les serveurs"
                    value={filterServer}
                    onChange={handleServerChange}
                    style={{ width: 180 }}
                  >
                    <Option value="all">Tous les serveurs</Option>
                    {uniqueServers.map(server => (
                      <Option key={server} value={server}>
                        {server}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    placeholder="Toutes les zones"
                    value={filterZone}
                    onChange={setFilterZone}
                    style={{ width: 220 }}
                  >
                    <Option value="all">Toutes les zones</Option>
                    {availableZones.map(zone => (
                      <Option key={zone} value={zone}>
                        {zone}
                      </Option>
                    ))}
                  </Select>

                  <Button
                    type={showDeleted ? 'primary' : 'default'}
                    onClick={() => setShowDeleted(!showDeleted)}
                  >
                    {showDeleted ? 'Masquer supprimés' : 'Afficher supprimés'}
                  </Button>

                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchProviders}
                    loading={loading}
                  >
                    Actualiser
                  </Button>
                </Space>
              </Col>

              <Col>
                <Space>
                  <Button
                    type="primary"
                    onClick={() => setShowOptimizedForm(true)}
                    className="bg-gradient-to-r from-purple-600 to-blue-600"
                    style={{
                      background: 'linear-gradient(to right, #9333ea, #2563eb)',
                      border: 'none',
                    }}
                  >
                    + Ajouter un provider
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        {/* Barre d'outils pour l'onglet Zones */}
        {activeTab === 'zones' && (
          <Card className="mb-4">
            <Row gutter={[16, 16]} align="middle">
              <Col flex="auto">
                <Space size="middle" wrap>
                  <Input
                    placeholder="Rechercher une zone..."
                    prefix={<SearchOutlined />}
                    value={zoneSearchText}
                    onChange={e => setZoneSearchText(e.target.value)}
                    style={{ width: 280 }}
                    allowClear
                  />

                  <Select
                    placeholder="Tous les serveurs"
                    value={zoneFilterServer}
                    onChange={setZoneFilterServer}
                    style={{ width: 180 }}
                  >
                    <Option value="all">Tous les serveurs</Option>
                    {uniqueServers.map(server => (
                      <Option key={server} value={server}>
                        {server}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    placeholder="Statut"
                    value={zoneFilterStatus}
                    onChange={setZoneFilterStatus}
                    style={{ width: 180 }}
                  >
                    <Option value="all">Tous les statuts</Option>
                    <Option value="active">Avec providers actifs</Option>
                    <Option value="inactive">Sans providers actifs</Option>
                  </Select>

                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchProviders}
                    loading={loading}
                  >
                    Actualiser
                  </Button>
                </Space>
              </Col>

              <Col>
                <Space>
                  <Button
                    type="primary"
                    onClick={() => setShowOptimizedForm(true)}
                    className="bg-gradient-to-r from-purple-600 to-blue-600"
                    style={{
                      background: 'linear-gradient(to right, #9333ea, #2563eb)',
                      border: 'none',
                    }}
                  >
                    + Ajouter un provider
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        )}
      </div>

      {/* Onglets */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Vue par Zone" key="zones">
          <Table
            columns={zoneColumns}
            dataSource={filteredZoneStats}
            rowKey="zone"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} zones`,
            }}
            scroll={{ x: 'max-content' }}
          />
        </Tabs.TabPane>

        <Tabs.TabPane tab="Vue par Provider" key="providers">
          <Table
            columns={providerColumns}
            dataSource={filteredProviders}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} providers`,
            }}
            scroll={{ x: 'max-content' }}
            rowClassName={record =>
              record.deleted_at ? 'bg-gray-100 opacity-60' : ''
            }
          />
        </Tabs.TabPane>
      </Tabs>

      {/* Formulaire optimisé en modal overlay */}
      {showOptimizedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl my-8">
            <OptimizedProviderForm
              onProviderCreated={handleProviderCreated}
              onClose={() => setShowOptimizedForm(false)}
            />
          </div>
        </div>
      )}

      {/* Modal des broadcasts par zone */}
      <Modal
        title={`Broadcasts de la zone: ${selectedZone}`}
        open={showBroadcastsModal}
        onCancel={() => {
          setShowBroadcastsModal(false);
          setSelectedZone(null);
          setZoneBroadcasts([]);
        }}
        width={1000}
        footer={null}
      >
        <div className="mb-4">
          <Tag color="blue">Total: {zoneBroadcasts.length}</Tag>
          <Tag color="green">
            Actifs: {zoneBroadcasts.filter(b => b.status === 'active').length}
          </Tag>
          <Tag color="default">
            Inactifs: {zoneBroadcasts.filter(b => b.status !== 'active').length}
          </Tag>
        </div>

        <Table
          columns={broadcastColumns}
          dataSource={zoneBroadcasts}
          rowKey="id"
          loading={loadingBroadcasts}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} broadcasts`,
          }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </Modal>
    </div>
  );
}
